import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { BehaviorSubject, combineLatest, map, Observable, tap } from 'rxjs';

import { SystemService } from '@offconon/core-api';
import { SharedUiVirtualscrollDropdownComponent } from '@offconon/shared/ui/shared-ui-virtualscroll-dropdown';
import {
  CommonSelectOption,
  PaginationAndSearch,
} from '@offconon/shared/ui/shared-ui-virtualscroll-multiselect';
import {
  CommonDropdownAndMultiselectService,
  DropdownData,
} from '@offconon/shared/utils/services/common-dropdown-and-multiselect';

import { LanguageDropdownModule } from './language-dropdown.module';
import { fetchLanguages, searchLanguage } from './store/language-dropdown.actions';
import { selectLanguageResponse } from './store/language-dropdown.selectors';
@Component({
  selector: 'offconon-langauge-dropdown-virtual-scroll-common',
  imports: [
    ButtonModule,
    SharedUiVirtualscrollDropdownComponent,
    TranslateModule,
    LanguageDropdownModule,
  ],
  providers: [CommonDropdownAndMultiselectService],
  templateUrl: './langauge-dropdown-virtual-scroll-common.component.html',
})
export class LangaugeDropdownVirtualScrollCommonComponent implements OnInit, OnChanges {
  @Input({ required: true }) languageFormControl: any;
  @Input() parameters: any;
  @Input() filter_languages: any;
  @Input() filter_invert_languages: any;
  @Input() selectFirst = true;
  @Input() selectedId: number;
  @Input() dropdownId = '';
  @Output() selectedLangaugeEvent = new EventEmitter<any>();

  public commonDropdownAndMultiselectService = inject(CommonDropdownAndMultiselectService);
  public systemService = inject(SystemService);
  public translateService = inject(TranslateService);
  public store = inject(Store);

  languageList$: Observable<CommonSelectOption[]>;

  dropdownData$ = new BehaviorSubject<
    DropdownData<{
      id?: number;
      name?: string;
      lang_en_name?: string;
      nativename?: string;
      sign?: boolean;
      translation_supported?: boolean;
      languages?: any;
      invert_languages?: any;
    }>
  >({ page: 1, pageSize: 150, dataList: [] });

  selectedLanguage = signal<CommonSelectOption | undefined>(undefined);

  searchJson: any = {};
  searchString = signal('');

  languageList = toSignal<CommonSelectOption[]>(
    combineLatest([
      this.store
        .select(selectLanguageResponse(this.dropdownId))
        .pipe(map((response) => response || { results: [] })),
      toObservable(this.searchString),
    ]).pipe(
      map(([data, search]) => {
        if (!data?.results) return [];

        const mappedResults = data.results.map(
          (item: any) =>
            ({
              name: this.translateService.instant('' + (item.lang_en_name || '')) || '-',
              id: item.id || 0,
              nativename: item.lang_native_name || '',
              mixed:
                this.translateService.instant('' + (item.lang_en_name || '')) +
                ' ' +
                (item.lang_native_name || ''),
              iso_code: item.iso_code || '',
            }) as CommonSelectOption,
        );

        if (!search || search.length < 2) return mappedResults;

        return mappedResults.filter((item: CommonSelectOption) =>
          item.name.toLowerCase().includes(search.toLowerCase()),
        );
      }),
      tap((data) => {
        if (data.length > 0) {
          if (this.selectFirst && this.selectedLanguage() === undefined) {
            this.selectedLanguage.set(data[0]);
            this.languageFormControl.patchValue(data[0].id);
          }
          if (this.selectedId) {
            this.selectedLanguage.set(data.find((item) => item.id === this.selectedId));
            this.languageFormControl.patchValue(this.selectedId);
          }
        }
      }),
    ),
  );
  languageResponse = toSignal(this.store.select(selectLanguageResponse(this.dropdownId)));

  ngOnChanges(changes: SimpleChanges): void {
    this.searchJson = {
      translation_supported: this.parameters?.translation_supported
        ? this.parameters?.translation_supported
        : true,
      sign: this.parameters?.sign ? this.parameters?.sign : undefined,
      languages: this.filter_languages ? this.filter_languages : undefined,
      invert_languages: this.filter_invert_languages ? this.filter_invert_languages : undefined,
    };

    this.fetchData();
    if (changes['languageFormControl']) {
      const selected = this.languageList()?.find(
        (item) => item.id === this.languageFormControl.value,
      );
      this.selectedLanguage.set(selected);
    }
    if (changes['selectedId']) {
      const selected = this.languageList()?.find((item) => item.id === this.selectedId);
      this.selectedLanguage.set(selected);
    }
  }

  ngOnInit(): void {
    this.fetchData();
  }

  fetchData() {
    this.store.dispatch(
      fetchLanguages({
        page: 1,
        pageSize: 150,
        searchJson: {
          ...this.searchJson,
        },
        dropdownId: this.dropdownId,
      }),
    );
  }

  select(event: CommonSelectOption) {
    this.selectedLanguage.set(event);
    this.languageFormControl.patchValue(event.id);
    this.selectedLangaugeEvent.emit(event);
  }

  onPagination(event: PaginationAndSearch) {
    this.searchString.set(event.searchString);
    this.store.dispatch(
      fetchLanguages({
        page: event.page,
        pageSize: event.pageSize,
        searchJson: {
          lang_en_name: event.searchString,
          ...this.searchJson,
        },
      }),
    );
  }

  onSearch(event: PaginationAndSearch) {
    this.searchString.set(event.searchString);
    if (event.searchString.length >= 2) {
      this.store.dispatch(
        searchLanguage({
          searchJson: {
            lang_en_name: event.searchString,
            ...this.searchJson,
          },
        }),
      );
    }
  }
}
